import { Stack, styled, Typography } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import { Input } from "@/components/elements/Input";
import { useDebounce } from "@/hooks/useDebounce";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { background as bgColor, text } from "@/theme/colors";
import { noContentCopy, spacing } from "@/theme/variables";
import { findScrollableParent } from "@/utils/dom";
import { InScreenScrollAnchor } from "@/components/commons/InScreenScrollAnchor";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { VocabularySummaryCard, VocabularyStudySection, VocabularyWordList, VocabularyFilterTabs } from "@/features/vocabulary/components";
import { ScrollToTopButton } from "@/features/vocabulary/components/ScrollToTopButton";
import { useVocabulary, VocabularyFilterType } from "@/infrastructure/adapters/vocabulary";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";
import { AppRoutes } from "@/routes/config";

/** Debounce delay for search — waits for IME composition to finish */
const SEARCH_DEBOUNCE_MS = 300;

export const VocabularyListPage: FC = () => {
  const { t } = useTranslation();
  const { categoryName } = useParams<{ categoryName: string }>();
  const { isTrial, isLoading: isTrialLoading } = useTrialStatus();
  const { settings } = useStudySettings();
  const showFurigana = settings?.isFuriganaEnabled ?? false;
  const shouldFetchVocabularies = !isTrialLoading && !isTrial;
  const { words, summary, error, isLoading } = useVocabulary(categoryName, { enabled: shouldFetchVocabularies });
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const rootRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node) setScrollParent(findScrollableParent(node));
  }, []);

  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<VocabularyFilterType>("all");

  /* Debounce search to avoid filtering on intermediate IME composition values (e.g., romaji "ka" → katakana "カ") */
  useDebounce(() => setSearchQuery(inputValue), [inputValue], SEARCH_DEBOUNCE_MS);

  const resetKey = `${filter}-${searchQuery}`;

  const filterOptions = [
    { value: "all" as const, label: t("vocabulary.filterAll") },
    { value: "mastered" as const, label: t("vocabulary.filterMastered") },
    { value: "not_mastered" as const, label: t("vocabulary.filterNotMastered") },
  ];

  const filteredWords = useMemo(() => {
    let result = words;

    if (filter !== "all") {
      result = result.filter((w) => w.status === filter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (w) => w.kanji.toLowerCase().includes(q) || w.furigana.toLowerCase().includes(q) || w.translation.toLowerCase().includes(q),
      );
    }

    return result;
  }, [words, filter, searchQuery]);

  if (isTrialLoading || isLoading) {
    return <LoadingArea />;
  }

  if (isTrial) {
    return <Navigate to={AppRoutes.study} replace />;
  }

  if (error) {
    return <Navigate to={AppRoutes.vocabularyCategoryList} replace />;
  }

  return (
    <Stack ref={rootRefCallback} flex={1} sx={noContentCopy}>
      <Stack gap={2} position="relative">
        {categoryName && (
          <Typography variant="sub16Bold" color={text.primary}>
            {categoryName}
          </Typography>
        )}
        <VocabularySummaryCard masteredCount={summary?.masteredCount ?? 0} />
        <VocabularyStudySection categoryName={categoryName} />
        <InScreenScrollAnchor triggerKey={resetKey} />
      </Stack>

      <StickySearchFilter>
        <Input
          nonControl
          size="lg"
          placeholder={t("vocabulary.searchPlaceholder")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <VocabularyFilterTabs value={filter} onChange={setFilter} options={filterOptions} />
      </StickySearchFilter>

      <Stack mt={2}>
        <VocabularyWordList words={filteredWords} showFurigana={showFurigana} resetKey={resetKey} scrollParent={scrollParent} />
        <ScrollToTopButton scrollParent={scrollParent} />
      </Stack>
    </Stack>
  );
};

export default VocabularyListPage;

const StickySearchFilter = styled(Stack)({
  position: "sticky",
  top: `-${spacing.sm}`,
  zIndex: 10,
  backgroundColor: bgColor.primary,
  marginLeft: `-${spacing.sm}`,
  marginRight: `-${spacing.sm}`,
  paddingLeft: spacing.sm,
  paddingRight: spacing.sm,
  paddingTop: spacing.sm,
  marginTop: spacing.xxs,
  gap: spacing.sm,
});
